/**
 * Which federated sign-in providers are actually configured, per Firebase project.
 *
 * WEB-02. The login page shipped "Continue with Google" and "Continue with Apple"
 * unconditionally, and on 2026-08-24 a direct query of Firebase Auth
 * (`identitytoolkit.googleapis.com/admin/v2/projects/<p>/defaultSupportedIdpConfigs`)
 * found:
 *
 *   gamuttestdb  ->  apple.com enabled, google.com enabled
 *   gamut-prod   ->  no federated providers configured at all
 *
 * So in production both buttons called providers that do not exist, and the only
 * way a user learned that was by clicking one and getting a popup error. A button
 * that cannot succeed should not be on screen.
 *
 * This map is deliberately keyed by PROJECT ID and version-controlled rather than
 * driven by an environment variable: the answer is a property of the Firebase
 * project, it changes only when somebody does console work, and a diff is the
 * right record of that.
 *
 * The console is necessary but NOT sufficient. "Enabled" in Firebase Auth does
 * not mean a user can sign in: Apple needs a per-environment Return URL on the
 * Services ID, which Firebase knows nothing about, so a provider can read
 * enabled everywhere and still be dead on web (prod Apple was, for a week, until
 * 2026-09-01 - see the gamut-prod entry below). The bar for adding a provider
 * here is one completed real sign-in in that environment, read back from
 * Firebase Auth (lastLoginAt on an account carrying that providerId), not a
 * console toggle. Adding a provider without that proof re-creates the exact
 * defect this fixes - pinned by src/lib/__tests__/federatedProviders.test.ts.
 */

export type FederatedProvider = 'google' | 'apple';

/**
 * Known projects and the providers configured in each. A project absent from this
 * map falls through to the safe default of none: an unknown project is a project
 * nobody has verified, and rendering a dead button is worse than rendering none.
 */
const CONFIGURED_BY_PROJECT: Readonly<Record<string, readonly FederatedProvider[]>> = {
    // Dev / CI. Both enabled 2026-08-24; real sign-ins completed with both
    // providers on dev web, re-verified 2026-09-01 after the Apple key rotation.
    gamuttestdb: ['google', 'apple'],
    // Production. Verified 2026-08-25 against Firebase Auth: BOTH enabled.
    // Google's toggle provisioned the project's first OAuth client; Apple reuses
    // the team's existing Services ID (aams.Gamut.service). The signing key is
    // Z39263RQA4 as of 2026-09-01 - the original AMHL379H28 leaked in Slack and
    // was rotated and revoked that day. Rotation is invisible to this file: the
    // key signs the client secret, it is not a provider toggle.
    //
    // VERIFIED END-TO-END 2026-09-01. Both providers completed a real sign-in on
    // prod web that day, confirmed by lastLoginAt in Firebase Auth rather than by
    // the UI. This entry is no longer a mirror of a console toggle alone.
    //
    // Getting there found a gap worth remembering. Apple on prod web first failed
    // with "invalid_request / Invalid web redirect url": the shared Services ID
    // aams.Gamut.service was missing the Return URL
    // https://gamut-prod.firebaseapp.com/__/auth/handler. Dev's equivalent was
    // present, which is why dev web had always worked. The 2026-08-25 note on this
    // entry claiming prod's return URL was "added alongside dev's" was simply
    // wrong, and nothing detected it for a week because the console toggle - which
    // is what this file mirrors - said "enabled" the whole time.
    //
    // The lesson for this file: "enabled in Firebase" is NOT the same claim as
    // "a user can sign in". Apple needs a per-environment Return URL on the
    // Services ID that Firebase knows nothing about, so this map can be truthful
    // and the button still dead. Before adding a provider here, complete one real
    // sign-in in that environment.
    //
    // Useful discriminator if it recurs: a bad signing key fails invalid_client,
    // a missing Return URL fails invalid_request. And Google succeeding on the
    // same page is a control - Firebase manages Google's redirect URIs, Apple's
    // are hand-kept, so Google OK + Apple redirect error isolates to the
    // Services ID rather than the app or the project.
    'gamut-prod': ['google', 'apple'],
};

export function federatedProvidersFor(projectId: string | undefined): readonly FederatedProvider[] {
    if (!projectId) return [];
    return CONFIGURED_BY_PROJECT[projectId] ?? [];
}

export const KNOWN_PROJECT_IDS = Object.keys(CONFIGURED_BY_PROJECT);
